import { useState, useEffect } from 'react';
import { axiosInstance } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { LogOut, Plus, Briefcase, MapPin, Calendar, User, FileText, Filter, X, Settings as SettingsIcon, Languages, Moon, Sun } from 'lucide-react';
import JobForm from './JobForm';
import JobDetails from './JobDetails';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/LanguageContext';
import { getTranslation } from '@/translations';
import { useTheme } from '@/ThemeContext';

  const formatDate = (dateString) => {
    if (!dateString) return 'No date set';
    const [year, month, day] = dateString.split('-');
    return `${month}-${day}-${year}`;
  };
const Dashboard = ({ onLogout }) => {
  const navigate = useNavigate();
  const { language, toggleLanguage } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const t = (key) => getTranslation(key, language);
  
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showJobForm, setShowJobForm] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [editingJob, setEditingJob] = useState(null);
  const username = localStorage.getItem('username');
  const userRole = localStorage.getItem('role');
  const isOwner = userRole === 'owner';

  // Filters
  const [clientFilter, setClientFilter] = useState('');
  const [employeeFilter, setEmployeeFilter] = useState('');
  const [employees, setEmployees] = useState([]);
  const [invoiceFilter, setInvoiceFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchJobs();
    if (isOwner) {
      fetchEmployees();
    }
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await axiosInstance.get('/users/employees');
      setEmployees(response.data);
    } catch (error) {
      console.error('Failed to fetch employees');
    }
  };

  const fetchJobs = async () => {
    try {
      const params = {};
      if (clientFilter) params.client_name = clientFilter;
      if (employeeFilter) params.created_by = employeeFilter;
      if (invoiceFilter && invoiceFilter !== 'all') params.invoice_status = invoiceFilter;
      
      const response = await axiosInstance.get('/jobs', { params });
      
      // Sort jobs chronologically (newest first)
      const sortedJobs = response.data.sort((a, b) => {
        return new Date(b.created_at) - new Date(a.created_at);
      });
      
      setJobs(sortedJobs);
    } catch (error) {
      toast.error('Failed to fetch jobs');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    setLoading(true);
    fetchJobs();
  };

  const clearFilters = () => {
    setClientFilter('');
    setEmployeeFilter('');
    setInvoiceFilter('all');
    setLoading(true);
    setTimeout(() => fetchJobs(), 100);
  };

  const handleJobCreated = () => {
    setShowJobForm(false);
    setEditingJob(null);
    fetchJobs();
  };

  const handleEditJob = (job) => {
    // Check if user can edit this job
    if (!isOwner && job.created_by !== username) {
      toast.error('You can only edit jobs you created');
      return;
    }
    setEditingJob(job);
    setShowJobForm(true);
  };

  const handleViewJob = (job) => {
    setSelectedJob(job);
  };

  const handleToggleInvoice = async (job, e) => {
    e.stopPropagation(); // Prevent opening job details
    
    // Optimistic update
    const newInvoiceStatus = !job.invoice_sent;
    
    // Update local state immediately
    setJobs(prevJobs => prevJobs.map(j => 
        j.id === job.id ? { ...j, invoice_sent: newInvoiceStatus } : j
    ));
    
    try {
      await axiosInstance.put(`/jobs/${job.id}`, {
        invoice_sent: newInvoiceStatus
      });
      
      toast.success(newInvoiceStatus ? t('invoice_sent') : 'Invoice marked as not sent');
      // No need to fetchJobs() here as we already updated the state
    } catch (error) {
      // Revert on error
      setJobs(prevJobs => prevJobs.map(j => 
          j.id === job.id ? { ...j, invoice_sent: !newInvoiceStatus } : j
      ));
      toast.error('Failed to update invoice status');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-sky-50 to-blue-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900" data-testid="dashboard">
      {/* Header */}
      <header className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-md border-b border-slate-200 dark:border-slate-700 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <div className="bg-gradient-to-br from-sky-400 to-blue-500 p-2 rounded-xl">
                <Briefcase className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">JobTracker Pro</h1>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Welcome, {username} {isOwner && <span className="text-blue-600 font-semibold">(Owner)</span>}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                data-testid="theme-toggle"
                onClick={toggleTheme}
                variant="outline"
                className="border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700"
                title={theme === 'light' ? 'Switch to Dark Mode' : 'Switch to Light Mode'}
              >
                {theme === 'light' ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
              </Button>
              <Button
                data-testid="language-toggle"
                onClick={toggleLanguage}
                variant="outline"
                className="border-slate-300 dark:border-slate-600 hover:bg-purple-50 dark:hover:bg-slate-700"
                title={language === 'en' ? 'Switch to Spanish' : 'Cambiar a Inglés'}
              >
                <Languages className="w-4 h-4 mr-2" />
                {language === 'en' ? 'ES' : 'EN'}
              </Button>
              <Button
                data-testid="settings-button"
                onClick={() => navigate('/settings')}
                variant="outline"
                className="border-slate-300 dark:border-slate-600 hover:bg-sky-50 dark:hover:bg-slate-700"
              >
                <SettingsIcon className="w-4 h-4 mr-2" />
                {t('settings')}
              </Button>
              <Button
                data-testid="logout-button"
                onClick={onLogout}
                variant="outline"
                className="border-slate-300 hover:bg-red-50 hover:border-red-300 hover:text-red-600"
              >
                <LogOut className="w-4 h-4 mr-2" />
                {t('logout')}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Action Bar */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-3xl font-bold text-slate-800 dark:text-slate-100">{t('all_jobs')}</h2>
            <p className="text-slate-600 dark:text-slate-400 mt-1">{jobs.length} {t('total_jobs')}</p>
          </div>
          <div className="flex gap-3">
            <Button
              data-testid="toggle-filters-button"
              onClick={() => setShowFilters(!showFilters)}
              variant="outline"
              className="border-slate-300"
            >
              <Filter className="w-4 h-4 mr-2" />
              {showFilters ? t('hide_filters') : t('show_filters')}
            </Button>
            <Button
              data-testid="create-job-button"
              onClick={() => {
                setShowJobForm(true);
                setEditingJob(null);
              }}
              className="bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white font-medium shadow-lg"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('create_new_job')}
            </Button>
          </div>
        </div>

        {/* Filters Section (All Users) */}
        {showFilters && (
          <Card className="mb-6 shadow-lg border-0 bg-white/90 dark:bg-slate-800/90">
            <CardHeader>
              <CardTitle className="text-lg dark:text-slate-100">{t('filter_jobs')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="client-filter">{t('client_name')}</Label>
                  <Input
                    id="client-filter"
                    data-testid="client-filter-input"
                    placeholder={t('search_by_client')}
                    value={clientFilter}
                    onChange={(e) => setClientFilter(e.target.value)}
                  />
                </div>
                {isOwner && (
                  <div className="space-y-2">
                    <Label htmlFor="employee-filter">{t('employee')}</Label>
                    <Select value={employeeFilter} onValueChange={setEmployeeFilter}>
                      <SelectTrigger data-testid="employee-filter-select">
                        <SelectValue placeholder={t('all_employees')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('all_employees')}</SelectItem>
                        {employees.map((emp) => (
                          <SelectItem key={emp.username} value={emp.username}>
                            {emp.username} {emp.role === 'owner' && `(${t('owner')})`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="invoice-filter">Invoice Status</Label>
                  <Select value={invoiceFilter} onValueChange={setInvoiceFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="All Statuses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="sent">Sent</SelectItem>
                      <SelectItem value="unsent">Unsent Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end gap-2">
                  <Button
                    data-testid="apply-filters-button"
                    onClick={applyFilters}
                    className="flex-1 bg-sky-500 hover:bg-sky-600"
                  >
                    {t('apply_filters')}
                  </Button>
                  <Button
                    data-testid="clear-filters-button"
                    onClick={clearFilters}
                    variant="outline"
                    className="flex-1"
                  >
                    <X className="w-4 h-4 mr-2" />
                    {t('clear')}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Jobs Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-sky-500 border-t-transparent"></div>
            <p className="mt-4 text-slate-600">Loading jobs...</p>
          </div>
        ) : jobs.length === 0 ? (
          <Card className="shadow-lg border-0 dark:bg-slate-800/90">
            <CardContent className="py-12 text-center">
              <Briefcase className="w-16 h-16 mx-auto text-slate-300 dark:text-slate-600 mb-4" />
              <h3 className="text-xl font-semibold text-slate-700 dark:text-slate-200 mb-2">{t('no_jobs_yet')}</h3>
              <p className="text-slate-500 dark:text-slate-400 mb-6">{t('create_first_job')}</p>
              <Button
                data-testid="create-first-job-button"
                onClick={() => setShowJobForm(true)}
                className="bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                {t('create_new_job')}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {jobs.map((job) => (
              <Card
                key={job.id}
                className="group hover:shadow-xl transition-all duration-300 border-0 bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm cursor-pointer"
                onClick={() => handleViewJob(job)}
                data-testid={`job-card-${job.id}`}
              >
                {job.photos && job.photos.length > 0 && (
                  <div className="h-48 overflow-hidden rounded-t-lg">
                    <img
                      src={job.photos[0]}
                      alt="Job preview"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                )}
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <CardTitle className="text-xl font-semibold text-slate-800 dark:text-slate-100 line-clamp-1">
                        {job.client_name}
                      </CardTitle>
                      <CardDescription className="flex items-center text-slate-600 dark:text-slate-400">
                        <MapPin className="w-4 h-4 mr-1" />
                        <span className="line-clamp-1">{job.address}</span>
                      </CardDescription>
                    </div>
                    <div className="flex flex-col gap-1">
                      {job.status === 'completed' && (
                        <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full font-medium">
                          ✓ {t('completed')}
                        </span>
                      )}
                      {job.invoice_sent && (
                        <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full font-medium">
                          ✓ {t('invoice_sent')}
                        </span>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex items-center text-sm text-slate-600 dark:text-slate-400">
                    <Calendar className="w-4 h-4 mr-2 text-sky-500" />
                    <span>{formatDate(job.date)}</span>
                  </div>
                  <div className="flex items-center text-sm text-slate-500 dark:text-slate-400">
                    <span className="text-xs">{t('created_on')}: {job.created_at ? new Date(job.created_at).toLocaleDateString() : ''}</span>
                  </div>
                  {job.price && (
                    <div className="flex items-center text-sm text-green-600 dark:text-green-400 font-semibold">
                      <span>💰 ${parseFloat(job.price).toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex items-center text-sm text-slate-600 dark:text-slate-400">
                    <User className="w-4 h-4 mr-2 text-blue-500" />
                    <span>{job.main_technician}</span>
                  </div>
                  {isOwner && job.created_by && (
                    <div className="flex items-center text-sm text-purple-600 dark:text-purple-400 font-medium">
                      <Briefcase className="w-4 h-4 mr-2" />
                      <span>{t('created_by')}: {job.created_by}</span>
                    </div>
                  )}
                  <div className="flex items-start text-sm text-slate-600 dark:text-slate-400">
                    <FileText className="w-4 h-4 mr-2 mt-0.5 text-purple-500" />
                    <span className="line-clamp-2">{job.description}</span>
                  </div>

                  {/* Quick Invoice Toggle Button */}
                  <div className="pt-3 mt-3 border-t border-slate-200">
                    <Button
                      data-testid={`invoice-toggle-${job.id}`}
                      onClick={(e) => handleToggleInvoice(job, e)}
                      variant={job.invoice_sent ? "default" : "outline"}
                      className={`w-full ${job.invoice_sent 
                        ? 'bg-blue-500 hover:bg-blue-600 text-white' 
                        : 'border-blue-300 text-blue-600 hover:bg-blue-50'
                      }`}
                      size="sm"
                    >
                      {job.invoice_sent ? (
                        <>
                          <span className="mr-2">✓</span> {t('invoice_sent')}
                        </>
                      ) : (
                        <>
                          <span className="mr-2">📄</span> {t('mark_invoice_button')}
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Job Form Modal */}
      {showJobForm && (
        <JobForm
          job={editingJob}
          onClose={() => {
            setShowJobForm(false);
            setEditingJob(null);
          }}
          onSuccess={handleJobCreated}
        />
      )}

      {/* Job Details Modal */}
      {selectedJob && (
        <JobDetails
          job={selectedJob}
          onClose={() => setSelectedJob(null)}
          onEdit={handleEditJob}
          onUpdate={fetchJobs}
        />
      )}
    </div>
  );
};

export default Dashboard;
